// import { crossfade } from 'svelte/transition';
import { quintInOut, quadInOut } from 'svelte/easing';
import type { CrossfadeParams, TransitionConfig } from 'svelte/transition';

// This module vendors Svelte's own `crossfade` so the outgoing node can be hidden
// outright (the `params.sending` branch below). That extra flag is not part of
// Svelte's public CrossfadeParams, hence the local param types.
type SendingParams = CrossfadeParams & { sending?: boolean };
type KeyedParams = SendingParams & { key: unknown };
type Fallback = (node: HTMLElement, params: SendingParams, intro: boolean) => TransitionConfig;

export const [send, receive] = crossfade({
  duration: (d) => Math.sqrt(d * 400),
  // easing: quintInOut,

  fallback(node, params) {
    const style = getComputedStyle(node);
    const transform = style.transform === 'none' ? '' : style.transform;

    return {
      duration: 600,
      easing: quintInOut,
      css: (t) => `
        opacity: ${t};
      `,
      // transform: ${transform} scale(${t});
    };
  },
});

export const squish = (node: any, params: any) => {
  return {
    delay: params.delay || 0,
    duration: params.duration || 400,
    css: (t: any, u: any) => {
      const eased = quadInOut(t);
      return `max-height:${eased * 90}; margin-bottom:${eased * 10}`;
    },
  };
};

function assign<T extends object, S extends object>(tar: T, src: S): T & S {
  for (const k in src) (tar as Record<string, unknown>)[k] = src[k];
  return tar as T & S;
}

function cubic_out(t: number) {
  const f = t - 1.0;
  return f * f * f + 1.0;
}

/**
 * A fixed, click-through layer on <body> that a drag ghost can finish its
 * journey in.
 *
 * The die that moves between two lists is animated in place, on the node that
 * has just been created inside the destination -- and that cannot be seen. The
 * destination sits inside `.skills`, which clips its descendants
 * (`overflow-y: auto`) and forms a stacking context (it has an animation in
 * effect), so the die is cropped for most of its journey and no z-index can
 * lift it out: z-index only competes within a stacking context. `.wood::before`
 * has `mix-blend-mode`, which caps it again at the row.
 */
let flightLayerElement: HTMLElement | null = null;

function flightLayer() {
  if (flightLayerElement?.isConnected) {
    return flightLayerElement;
  }

  flightLayerElement = document.createElement('div');
  flightLayerElement.dataset.flightLayer = '';
  Object.assign(flightLayerElement.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    margin: '0',
    padding: '0',
    border: '0',
    display: 'block',
    overflow: 'visible',
    pointerEvents: 'none',
    // Clamped to the maximum by every browser, so nothing can sit above it.
    zIndex: '2147483647',
  });
  document.body.appendChild(flightLayerElement);

  return flightLayerElement;
}

/** How long <Draggable> takes to slide back; see its top/left transition. */
const SNAP_BACK_MS = 460;

/** Mirrors in progress, so one can be handed on to another element mid-flight. */
const activeMirrors = new Map<unknown, { stop: (handOver: boolean) => HTMLElement | null }>();

/** A drag ghost kept alive at the drop, waiting for its die to start moving. */
const pendingGhosts = new Map<
  unknown,
  { element: HTMLElement; timeout: ReturnType<typeof setTimeout> }
>();

/**
 * Keeps a <Draggable>'s ghost alive past the end of the drag -- or, for a tap,
 * leaves a copy of the item itself to do the same job.
 *
 * The ghost is the one thing here that already renders correctly over
 * everything, being portalled to the body and following the cursor. It used to
 * be destroyed the instant the mouse came up. Kept alive, it can stand in for
 * the die while the die makes a journey that cannot be seen.
 *
 * `key` is the die's id, which is also the key its transition uses.
 */
export function handOffFlight(key: unknown, ghost: HTMLElement, source: HTMLElement | null = null) {
  if (key === undefined || key === null || !ghost || typeof document === 'undefined') {
    return false;
  }

  const from = ghost.getBoundingClientRect();
  if (!from.width || !from.height) {
    return false;
  }

  releaseGhost(key);

  // A copy, so Svelte keeps ownership of the ghost it rendered and stays free
  // to tear it down with its component.
  const element = ghost.cloneNode(true) as HTMLElement;
  element.style.cssText = '';
  Object.assign(element.style, {
    position: 'fixed',
    left: `${from.left}px`,
    top: `${from.top}px`,
    width: `${from.width}px`,
    height: `${from.height}px`,
    margin: '0',
    pointerEvents: 'none',
    // The copy keeps the classes that make it look like a die, and some of
    // those hide it once the drag it belonged to has ended.
    display: 'block',
    visibility: 'visible',
    opacity: '1',
  });
  // .draggable transitions top/left with !important, which would leave the
  // copy trailing a beat behind the die it follows. A drag ghost escapes that by
  // carrying .dragging; a copy taken on a tap does not.
  element.style.setProperty('transition', 'none', 'important');
  flightLayer().appendChild(element);

  // Backstop for a ghost nothing ever stands in for, so one cannot end up
  // welded to the screen.
  const timeout = setTimeout(() => releaseGhost(key), 150);
  pendingGhosts.set(key, { element, timeout });

  /**
   * Start standing in immediately, on the element the die is leaving from, so
   * the ghost never hangs motionless. Two things can happen from here:
   *
   * - the die moves to another list, and the arriving half takes this same
   *   ghost over mid-flight and follows it the rest of the way;
   * - it was dropped somewhere that would not take it, and slides home on
   *   <Draggable>'s own top/left transition. That trip is invisible too, for a
   *   different reason: nothing clips it, but .column.player comes before
   *   .column.board in the DOM, so a die crossing into the board's half is
   *   simply painted underneath it.
   */
  if (source?.isConnected) {
    mirrorNode(key, source, SNAP_BACK_MS, source.firstElementChild as HTMLElement | null);
  }

  return true;
}

function releaseGhost(key: unknown) {
  const pending = pendingGhosts.get(key);
  if (!pending) {
    return;
  }
  clearTimeout(pending.timeout);
  pending.element.remove();
  pendingGhosts.delete(key);
}

/**
 * Puts the ghost over the die and keeps it there for the whole journey.
 *
 * Nothing here works out where the die is going, which is what every previous
 * attempt got wrong. The die already knows: its own animation is running and
 * lands in the right place -- it just cannot be seen doing it. So the ghost is
 * moved onto the die's box once a frame and the die itself is hidden, which
 * makes the ghost a visible stand-in following a trajectory it never has to
 * understand.
 */
function mirrorNode(
  key: unknown,
  node: HTMLElement,
  duration: number,
  /**
   * What to hide while the ghost stands in, when that is not the element being
   * followed. <Draggable>'s own element carries a Svelte-bound `style`
   * attribute, and Svelte rewrites that wholesale on every update -- which
   * wipes an imperative `visibility: hidden` straight back off again. Its child
   * has no bound style, so it can be hidden and stay hidden.
   */
  hideTarget?: HTMLElement | null,
) {
  let ghost: HTMLElement | null = null;

  const pending = pendingGhosts.get(key);
  if (pending) {
    clearTimeout(pending.timeout);
    pendingGhosts.delete(key);
    ghost = pending.element;
  } else {
    // Already standing in for this die somewhere else. A drop starts by
    // mirroring the element it left from, and if it turns out to be moving to
    // another list the arriving half takes the same ghost over mid-flight --
    // otherwise there is a visible seam where the ghost sits still.
    ghost = activeMirrors.get(key)?.stop(true) ?? null;
  }

  if (!ghost) {
    return false;
  }

  const flying = ghost;
  const hidden = hideTarget ?? node;
  const previousVisibility = hidden.style.visibility;
  hidden.style.visibility = 'hidden';

  // A shade past the end, so the ghost is not taken away a frame before the die
  // it is standing in for has finished arriving.
  const until = Date.now() + duration + 32;
  let running = true;

  /** Ends this mirror, either handing the ghost on or retiring it. */
  const stop = (handOver: boolean) => {
    if (!running) {
      return null;
    }
    running = false;
    activeMirrors.delete(key);
    hidden.style.visibility = previousVisibility;

    if (handOver) {
      return flying;
    }
    flying.remove();
    return null;
  };

  activeMirrors.set(key, { stop });

  const step = () => {
    if (!running) {
      return;
    }

    const box = node.getBoundingClientRect();

    // A zero box means the node has gone -- removed, or its list hid it.
    if (!box.width || !box.height) {
      stop(false);
      return;
    }

    flying.style.left = `${box.left}px`;
    flying.style.top = `${box.top}px`;
    flying.style.width = `${box.width}px`;
    flying.style.height = `${box.height}px`;

    if (Date.now() >= until) {
      stop(false);
      return;
    }

    requestAnimationFrame(step);
  };

  requestAnimationFrame(step);
  return true;
}

export function crossfade({ fallback, ...defaults }: SendingParams & { fallback?: Fallback }) {
  const to_receive = new Map<unknown, HTMLElement>();
  const to_send = new Map<unknown, HTMLElement>();

  /**
   * Where each leaving element was, measured while it was still on screen.
   *
   * The leaving half hides itself 10ms after it starts, and the arriving half
   * measures that same element to work out where to travel from. When the two
   * halves land more than 10ms apart the arriving half measures a display:none
   * element, which reports 0x0 at 0,0 -- so the die flew in from the top left
   * corner of the window instead of from the list it left.
   */
  const sent_rects = new Map<unknown, DOMRect>();

  function crossfade(
    from_node: Element,
    node: HTMLElement,
    params: SendingParams,
  ): TransitionConfig {
    const {
      delay = 0,
      duration = (d: number) => Math.sqrt(d) * 30,
      easing = cubic_out,
    } = assign(assign({}, defaults), params);
    const key = (params as KeyedParams).key;

    // Taken while it is still on screen, for the arriving half to use.
    if (!params.sending) {
      sent_rects.set(key, node.getBoundingClientRect());
    }

    const live = from_node.getBoundingClientRect();
    const recorded = sent_rects.get(key);

    // Prefer the recorded rect: by now the element it came from may already be
    // display:none, which measures as 0x0 at the origin.
    const from = params.sending && recorded && (!live.width || !live.height) ? recorded : live;

    if (params.sending) {
      sent_rects.delete(key);
    }

    const to = node.getBoundingClientRect();
    const dx = from.left - to.left;
    const dy = from.top - to.top;
    // const dw = from.width / to.width;
    // const dh = from.height / to.height;
    const d = Math.sqrt(dx * dx + dy * dy);
    const style = getComputedStyle(node);
    const transform = style.transform === 'none' ? '' : style.transform;
    let opacity = +style.opacity;
    if (!params.sending) {
      opacity = 0;
      setTimeout(() => {
        node.style.display = 'none';
        node.style.opacity = '0';
      }, 10);
    }

    const ms = typeof duration === 'function' ? duration(d) : duration;

    // The arriving half. If a drag left a ghost behind, it stands in for this
    // node for the trip -- the animation below is unchanged and still does the
    // moving, it simply happens out of sight with the ghost tracing it in a
    // layer nothing can clip.
    if (params.sending) {
      mirrorNode(key, node, ms);
    }

    return {
      delay,
      duration: ms,
      easing,
      css: (t, u) => `
         z-index: 11111111111111111;
         opacity: ${opacity};
         transform-origin: center center;
         transform: ${transform} translate(${u * dx}px,${u * dy}px);
       `,
    };
  }

  function transition(
    items: Map<unknown, HTMLElement>,
    counterparts: Map<unknown, HTMLElement>,
    intro: boolean,
  ) {
    return (node: HTMLElement, params: KeyedParams): (() => TransitionConfig) => {
      items.set(params.key, node);
      return () => {
        if (counterparts.has(params.key)) {
          // Non-null: guarded by the `has` check on the line above.
          const other_node = counterparts.get(params.key)!;
          counterparts.delete(params.key);
          params.sending = intro;
          return crossfade(other_node, node, params);
        }
        // if the node is disappearing altogether
        // (i.e. wasn't claimed by the other list)
        // then we need to supply an outro
        items.delete(params.key);
        // An empty config is a valid no-op transition. Svelte's own copy returns
        // `undefined` here and suppresses the resulting type error; this app always
        // passes a fallback, so the empty branch is unreachable in practice.
        return fallback ? fallback(node, params, intro) : {};
      };
    };
  }
  return [transition(to_send, to_receive, false), transition(to_receive, to_send, true)];
}
